import type { GUID, NodeChange } from '@open-pencil/kiwi/fig/codec'
import { guidToString } from '@open-pencil/kiwi/fig/guid'

import { cloneRecord } from '../node-change/clone'
import { mergeVariableConsumptionMaps } from '../node-change/variable/bindings'
import {
  bindSourceProperties,
  componentBindings,
  instanceBindings,
  type BoundPropertyClaim,
  type PropertyBinding
} from './bindings/properties'
import {
  declareVariableBindingUnits,
  declareSourceVariableBindingUnits
} from './bindings/variables'
import { applyDerivedEntry } from './derived-symbol-data'
import {
  ownLayers,
  type AssignmentGroup,
  type Owner,
  type PropertyLayer,
  type StructuralLayer
} from './layers'
import {
  findSegment,
  isRootGuid,
  pathError,
  resolveOccurrencePath,
  SegmentError
} from './occurrence/path'
import type { InstanceOccurrence, InterpretInstanceOptions } from './occurrence/types.override'
import { applyPlacedConstraints } from './resize'
import { applyInstanceLayoutScale } from './scale/layout'
import {
  createSourceIndex,
  findStaticSegment,
  idOf,
  readOverrideKey,
  recordMatches,
  resolvesInSourceComponent,
  sameGuid,
  terminalComponent,
  type SourceIndex
} from './source-index'
import { invalidateInheritedTextData } from './text-provenance'
import {
  assignedSlotContent,
  type ComponentPropAssignment,
  type DerivedSymbolOverride
} from './types'

/**
 * Figma's instance model reduces to three things: an instance expands its component's
 * subtree; owners contribute layers, each a partial record at a path relative to that
 * owner; and the outermost owner wins where layers overlap. A swap and a property
 * assignment are structural layers, everything else is a property layer.
 *
 * Structural layers are routed down to the instance they configure before it expands,
 * so every occurrence expands exactly once with its effective component and complete
 * assignment list. Property layers are applied by their declaring owner onto the built
 * subtree, so their values stay in that owner's coordinate space; outer owners apply
 * after inner ones by construction. Saved derived data is a cache, applied last.
 */

export { resolveOccurrencePath } from './occurrence/path'

export function interpretInstance(
  changes: readonly NodeChange[],
  instanceId: string,
  options: InterpretInstanceOptions = {}
): InstanceOccurrence {
  return createOccurrenceInterpreter(changes).instance(instanceId, options)
}

export function interpretComponent(
  changes: readonly NodeChange[],
  componentId: string,
  options: InterpretInstanceOptions = {}
): InstanceOccurrence {
  return createOccurrenceInterpreter(changes).component(componentId, options)
}

/** One source index per document; evaluation state remains local to each call. */
export function createOccurrenceInterpreter(changes: readonly NodeChange[]) {
  const index = createSourceIndex(changes)
  return {
    instance: (id: string, options: InterpretInstanceOptions = {}) =>
      interpretRoot(index, id, 'INSTANCE', options),
    component: (id: string, options: InterpretInstanceOptions = {}) =>
      interpretRoot(index, id, 'SYMBOL', options),
    page: (id: string, options: InterpretInstanceOptions = {}) =>
      interpretRoot(index, id, 'CANVAS', options)
  }
}

function interpretRoot(
  index: SourceIndex,
  rootId: string,
  expectedType: 'INSTANCE' | 'SYMBOL' | 'CANVAS',
  options: InterpretInstanceOptions
): InstanceOccurrence {
  const { sources, children } = index
  const expanding = new Set<string>()
  /** Fields set by an owner's assignment, by the rank of that owner. */
  const assignedFields = new WeakMap<InstanceOccurrence, Map<string, number>>()
  /** Components an occurrence expanded before an outer decision replaced them. */
  const replacedComponents = new WeakMap<InstanceOccurrence, readonly GUID[]>()

  const defaultInstanceName = (occurrence: InstanceOccurrence): string | undefined => {
    const source = sources.get(occurrence.sourceId)
    if (source?.type === 'INSTANCE') return occurrence.properties.name
    const parentGuid = source?.parentIndex?.guid
    const parent = parentGuid ? sources.get(guidToString(parentGuid)) : undefined
    return parent?.isStateGroup === true ? parent.name : occurrence.properties.name
  }

  /** A component and its override key both address the root of an instance's expansion. */
  const componentKeys = (guid: GUID | undefined): GUID[] => {
    if (!guid) return []
    const key = readOverrideKey(sources.get(guidToString(guid))?.overrideKey)
    return key ? [guid, key] : [guid]
  }

  /** A layer written against a component that an outer decision replaced is stale, not wrong. */
  const isStaleLayer = ({ boundary }: StructuralLayer): boolean =>
    boundary?.replaced.some((component) =>
      resolvesInSourceComponent(index, component, boundary.path)
    ) ?? false

  /**
   * An address that resolves to nothing is a record Figma kept after deleting the node it
   * named, and a swap is no different: its replacement can be present while the layer it
   * replaced is gone. An ambiguous address means the path is wrong, which stays fatal.
   */
  const unresolvedStructural = (layer: StructuralLayer, cause: SegmentError): void => {
    if (cause.count === 0 && isStaleLayer(layer)) return
    const error = pathError(layer.owner.id, layer.owner.mainComponentId, layer.declaredPath, cause)
    if (cause.count !== 0 || !options.onUnresolvedAssignment) throw error
    layer.owner.unresolved.push({
      ...error.diagnostic,
      assignments: structuredClone(layer.assignments)
    })
  }

  /** Dispatch structural layers to the direct child whose static subtree holds their first segment. */
  const routeToChildren = (
    id: string,
    layers: readonly StructuralLayer[]
  ): Map<string, StructuralLayer[]> => {
    const routed = new Map<string, StructuralLayer[]>()
    for (const layer of layers) {
      const [segment] = layer.path
      const { count, topChild } = findStaticSegment(index, id, segment)
      if (!topChild?.guid) {
        unresolvedStructural(layer, new SegmentError(count, segment))
        continue
      }
      const childId = guidToString(topChild.guid)
      const direct = recordMatches(topChild, segment)
      const next = { ...layer, path: direct ? layer.path.slice(1) : layer.path }
      const bucket = routed.get(childId)
      if (bucket) bucket.push(next)
      else routed.set(childId, [next])
    }
    return routed
  }

  interface RootResolution {
    effective: GUID | undefined
    /** Components superseded on the way to `effective`, innermost decision first. */
    replaced: GUID[]
    groups: AssignmentGroup[]
    descendant: StructuralLayer[]
  }

  /** Components an occurrence would have expanded before outer bindings replaced them. */
  const bindingHistory = (
    raw: NodeChange,
    effective: GUID | undefined,
    superseded: readonly GUID[]
  ): GUID[] => {
    const original = raw.symbolData?.symbolID
    if (!effective) return []
    return [...(original ? [original] : []), ...superseded].filter(
      (component) => !sameGuid(component, effective)
    )
  }

  /**
   * Root layers select the effective component and the complete assignment list, in
   * inner-to-outer order so later entries win. Everything else addresses a descendant.
   */
  const resolveRoot = (
    raw: NodeChange,
    source: NodeChange,
    superseded: readonly GUID[],
    structural: readonly StructuralLayer[],
    rank: number
  ): RootResolution => {
    let effective = source.symbolData?.symbolID
    const replaced = bindingHistory(raw, effective, superseded)
    const keys = [...componentKeys(raw.symbolData?.symbolID), ...componentKeys(effective)]
    const isKey = (guid: GUID): boolean => keys.some((key) => sameGuid(key, guid))
    const groups: AssignmentGroup[] = [
      { assignments: (source.componentPropAssignments ?? []) as ComponentPropAssignment[], rank }
    ]
    const descendant: StructuralLayer[] = []
    const swapRoot = (swap: GUID): void => {
      if (!effective) throw new Error('Swap target is not an instance')
      if (!sameGuid(effective, swap)) replaced.push(effective)
      effective = swap
      // Later layers may address the root through the replacement's identity.
      for (const key of componentKeys(effective)) if (!isKey(key)) keys.push(key)
    }
    for (const layer of structural) {
      const addressesRoot = layer.path.length === 0 || isKey(layer.path[0])
      if (!addressesRoot || layer.path.length > 1) {
        descendant.push(addressesRoot ? { ...layer, path: layer.path.slice(1) } : layer)
        continue
      }
      if (layer.swap) swapRoot(layer.swap)
      if (layer.assignments.length)
        groups.push({ assignments: layer.assignments, rank: layer.owner.rank })
    }
    return { effective, replaced, groups, descendant }
  }

  const applyPropertyClaim = (
    owner: InstanceOccurrence,
    ownerRank: number,
    target: InstanceOccurrence,
    layer: PropertyLayer
  ): void => {
    // An outer owner's assignment supersedes this owner's explicit value for the same field.
    const bound = assignedFields.get(target)
    const retained = Object.fromEntries(
      Object.entries(layer.props).filter(([field]) => {
        const rank = bound?.get(field)
        return rank === undefined || rank >= ownerRank
      })
    )
    if (Object.keys(retained).length === 0) return
    declareVariableBindingUnits(target, retained as NodeChange)
    invalidateInheritedTextData(target.properties, retained)
    Object.assign(
      target.properties,
      cloneRecord(retained),
      mergeVariableConsumptionMaps(target.properties, retained as NodeChange)
    )
    if ('name' in retained) target.hasOwnName = true
    owner.propertyClaims.push({
      declaredBy: owner.sourceId,
      path: structuredClone(layer.path),
      properties: structuredClone(retained)
    })
  }

  /**
   * A claim that addressed a component an instance on its path no longer expands is
   * stale, not wrong: some outer decision replaced that subtree after it was written.
   */
  const isRemovedTarget = (owner: InstanceOccurrence, path: readonly GUID[]): boolean => {
    let current = owner
    for (const [position, segment] of path.entries()) {
      const rest = path.slice(position)
      const replaced = replacedComponents.get(current) ?? []
      if (replaced.some((component) => resolvesInSourceComponent(index, component, rest)))
        return true
      if (position === 0 && isRootGuid(current, segment)) continue
      try {
        current = findSegment(current, segment)
      } catch (error) {
        if (!(error instanceof SegmentError)) throw error
        return false
      }
    }
    return false
  }

  /** Resolve an owner's declared path in its built subtree, or decide how to skip it. */
  const resolveClaimTarget = (
    owner: InstanceOccurrence,
    path: readonly GUID[]
  ): InstanceOccurrence | undefined => {
    try {
      return resolveOccurrencePath(owner, path)
    } catch (cause) {
      if (!(cause instanceof SegmentError)) throw cause
      const error = pathError(owner.sourceId, owner.mainComponentId, path, cause)
      if (error.diagnostic.reason === 'missing-target' && isRemovedTarget(owner, path))
        return undefined
      if (!options.onUnresolvedProperty) throw error
      options.onUnresolvedProperty(error.diagnostic)
      return undefined
    }
  }

  const applyDerivedBounds = (owner: InstanceOccurrence, source: NodeChange): void => {
    if (!options.derivedBounds) return
    const derived = source.derivedSymbolData as DerivedSymbolOverride[] | undefined
    for (const entry of derived ?? []) {
      const path = entry.guidPath?.guids
      if (!path?.length) continue
      const target = resolveClaimTarget(owner, path)
      if (!target || target === owner) continue // Placed root bounds belong to its NodeChange.
      applyDerivedEntry(target, entry)
    }
  }

  /** Bind the record's fields from the enclosing component scope, recording provenance. */
  const bindRecord = (
    raw: NodeChange,
    bindings: readonly PropertyBinding[]
  ): {
    source: NodeChange
    claims: BoundPropertyClaim[]
    /** Fields set by an owner's assignment, by that owner's rank. */
    bound: Map<string, number>
    /** Swap values earlier owners assigned before a later one replaced them. */
    superseded: GUID[]
    /** The content frame an assignment put in this slot frame. */
    slotContent: string | undefined
  } => {
    const claims: BoundPropertyClaim[] = []
    const bound = new Map<string, number>()
    const superseded: GUID[] = []
    let slotContent: string | undefined
    const source = bindSourceProperties(raw, bindings, (claim, binding) => {
      claims.push(claim)
      if (binding.origin === 'assignment' && binding.rank !== undefined)
        bound.set(claim.field, binding.rank)
      if (claim.field === 'symbolData')
        for (const value of binding.superseded ?? [])
          if (value.guidValue) superseded.push(value.guidValue)
      if (claim.field === 'slotContent') {
        const content = assignedSlotContent(binding.value)
        slotContent = content ? guidToString(content) : undefined
      }
    })
    if (slotContent && !sources.has(slotContent)) {
      if (!options.onMissingSlotContent) throw new Error(`Missing slot content ${slotContent}`)
      options.onMissingSlotContent({ slotId: idOf(raw) ?? '', slotContentId: slotContent })
      slotContent = undefined
    }
    return { source, claims, bound, superseded, slotContent }
  }

  /**
   * When expansion would re-enter a source already on the stack, report and soft-fail
   * if a diagnostic receiver is supplied; otherwise refuse the document.
   */
  const refuseCycle = (ownerId: string, componentId: string): boolean => {
    if (!expanding.has(componentId)) return false
    if (!options.onCyclicExpansion) throw new Error(`Cyclic component expansion at ${componentId}`)
    options.onCyclicExpansion({ ownerId, componentId })
    return true
  }

  /**
   * Expand one source record. `layers` are structural layers addressed relative to this
   * expansion: an empty path configures this record itself, a longer path a descendant.
   */
  const expand = (
    id: string,
    bindings: readonly PropertyBinding[],
    layers: readonly StructuralLayer[],
    rank: number
  ): InstanceOccurrence => {
    if (expanding.has(id)) throw new Error(`Cyclic component expansion at ${id}`)
    const raw = sources.get(id)
    if (!raw) throw new Error(`Missing source node ${id}`)
    const record = bindRecord(raw, bindings)
    const { source } = record
    expanding.add(id)
    try {
      const owner: Owner = { id, rank, mainComponentId: null, unresolved: [] }
      const own = ownLayers(source, owner)
      const structural = [...own.structural, ...layers].sort((a, b) => b.owner.rank - a.owner.rank)
      const root = resolveRoot(raw, source, record.superseded, structural, rank)
      let subtree: Subtree
      if (root.effective) subtree = expandBase(owner, { ...root, effective: root.effective }, rank)
      else if (record.slotContent)
        subtree = expandSlotContent(id, record.slotContent, root.descendant, rank)
      else subtree = expandChildren(id, source, bindings, root.groups, root.descendant, rank)
      const occurrence = createOccurrence(id, raw, source, root, subtree, record.claims)
      if (record.slotContent) occurrence.slotContentId = record.slotContent
      assignedFields.set(occurrence, record.bound)
      if (subtree.base && root.replaced.length) replacedComponents.set(occurrence, root.replaced)
      declareSourceVariableBindingUnits(occurrence, source)
      const claims = subtree.dangling
        ? own.claims.filter(({ path }) => path.length === 1 && isRootGuid(occurrence, path[0]))
        : own.claims
      finishOccurrence(occurrence, subtree.base, source, claims, rank, !subtree.dangling)
      for (const diagnostic of owner.unresolved)
        options.onUnresolvedAssignment?.({
          ...diagnostic,
          mainComponentId: occurrence.mainComponentId
        })
      return occurrence
    } finally {
      expanding.delete(id)
    }
  }

  interface Subtree {
    base: InstanceOccurrence | null
    children: InstanceOccurrence[]
    /** The component is gone; only the record itself remains addressable. */
    dangling?: true
  }

  /**
   * The component expansion receives the assignments as its own root layers and the
   * descendant layers unchanged: both address the same subtree.
   */
  const expandBase = (
    owner: Owner,
    { effective, replaced, groups, descendant }: RootResolution & { effective: GUID },
    rank: number
  ): Subtree => {
    const componentId = guidToString(effective)
    if (!sources.has(componentId)) {
      // Layers addressed into the missing subtree have nothing to configure.
      if (!options.onMissingComponent) throw new Error(`Missing source node ${componentId}`)
      options.onMissingComponent({ ownerId: owner.id, componentId })
      return { base: null, children: [], dangling: true }
    }
    // Self-referential symbolID or a longer swap cycle: keep the instance childless.
    if (refuseCycle(owner.id, componentId)) return { base: null, children: [], dangling: true }
    owner.mainComponentId = terminalComponent(index, componentId)
    const crossing = descendant.map((layer) =>
      replaced.length ? { ...layer, boundary: { replaced, path: layer.path } } : layer
    )
    const rootLayers = groups
      .filter((group) => group.assignments.length)
      .map((group): StructuralLayer => ({
        owner: { ...owner, rank: group.rank },
        declaredPath: [],
        path: [],
        assignments: group.assignments
      }))
    const base = expand(componentId, [], [...rootLayers, ...crossing], rank + 1)
    return { base, children: base.children }
  }

  /** Expand indexed children, skipping any whose source is already on the stack. */
  const expandIndexedChildren = (
    ownerId: string,
    childSourceId: string,
    bindings: readonly PropertyBinding[],
    descendant: readonly StructuralLayer[],
    rank: number
  ): InstanceOccurrence[] => {
    const routed = routeToChildren(childSourceId, descendant)
    const expanded: InstanceOccurrence[] = []
    for (const child of children.get(childSourceId) ?? []) {
      if (!child.guid) throw new Error('Indexed child has no GUID')
      const childId = guidToString(child.guid)
      if (refuseCycle(ownerId, childId)) continue
      expanded.push(expand(childId, bindings, routed.get(childId) ?? [], rank + 1))
    }
    return expanded
  }

  /** A component definition starts a binding scope; ordinary containers pass theirs through. */
  const expandChildren = (
    id: string,
    source: NodeChange,
    bindings: readonly PropertyBinding[],
    groups: readonly AssignmentGroup[],
    descendant: readonly StructuralLayer[],
    rank: number
  ): Subtree => {
    const scoped =
      source.type === 'SYMBOL'
        ? groups.reduce(
            (result, group) => instanceBindings(result, group.assignments, group.rank),
            componentBindings(source)
          )
        : bindings
    return {
      base: null,
      children: expandIndexedChildren(id, id, scoped, descendant, rank)
    }
  }

  /**
   * Slot content is the instance's own layers, saved under a content frame. Component
   * property bindings do not reach into a slot, so its layers expand outside that scope.
   */
  const expandSlotContent = (
    ownerId: string,
    contentId: string,
    descendant: readonly StructuralLayer[],
    rank: number
  ): Subtree => ({
    base: null,
    children: expandIndexedChildren(ownerId, contentId, [], descendant, rank)
  })

  const namesMissingSlotContent = (assignment: ComponentPropAssignment): boolean => {
    const content = assignedSlotContent(assignment.varValue?.value)
    return !!content && !sources.has(guidToString(content))
  }

  /** Identity and provenance an occurrence inherits from the subtree it expands. */
  const inheritedFromBase = (
    base: InstanceOccurrence | null
  ): Pick<
    InstanceOccurrence,
    | 'mainComponentId'
    | 'mainComponentOverrideKey'
    | 'propertyClaims'
    | 'variableBindingScales'
    | 'hasOwnName'
  > => {
    if (!base) {
      return {
        mainComponentId: null,
        mainComponentOverrideKey: undefined,
        propertyClaims: [],
        variableBindingScales: {},
        hasOwnName: false
      }
    }
    return {
      mainComponentId: base.mainComponentId ?? base.sourceId,
      mainComponentOverrideKey: base.mainComponentOverrideKey ?? base.overrideKey,
      propertyClaims: structuredClone(base.propertyClaims),
      variableBindingScales: { ...base.variableBindingScales },
      hasOwnName: sources.get(base.sourceId)?.type === 'INSTANCE' && base.hasOwnName
    }
  }

  const createOccurrence = (
    id: string,
    raw: NodeChange,
    source: NodeChange,
    { effective, replaced, groups }: RootResolution,
    { base, children: expanded }: Subtree,
    bindingClaims: BoundPropertyClaim[]
  ): InstanceOccurrence => {
    const original = raw.symbolData?.symbolID
    const [, sourceComponentOverrideKey] = componentKeys(original)
    const occurrence: InstanceOccurrence = {
      sourceId: id,
      sourceComponentId: original,
      sourceComponentOverrideKey,
      ...inheritedFromBase(base),
      bindingClaims,
      overrideKey: readOverrideKey(source.overrideKey),
      // The record is the top layer of its own root: its fields are placed state.
      properties: {
        ...base?.properties,
        ...source,
        ...(effective && source.symbolData
          ? { symbolData: { ...source.symbolData, symbolID: effective } }
          : {}),
        ...mergeVariableConsumptionMaps(base?.properties ?? {}, source)
      },
      children: expanded
    }
    if (source.type === 'INSTANCE')
      occurrence.properties.componentPropAssignments = groups
        .flatMap((g) => g.assignments)
        // A slot whose content frame is gone falls back to its component's content.
        .filter((assignment) => !namesMissingSlotContent(assignment))
    // A swapped instance without a name of its own takes the replacement's default name.
    if (base && replaced.length && !occurrence.hasOwnName)
      occurrence.properties.name = base.defaultInstanceName ?? base.properties.name
    occurrence.defaultInstanceName = defaultInstanceName(occurrence)
    return occurrence
  }

  /** Apply this owner's property layers, then its placed geometry and saved caches. */
  const finishOccurrence = (
    occurrence: InstanceOccurrence,
    base: InstanceOccurrence | null,
    source: NodeChange,
    claims: readonly PropertyLayer[],
    rank: number,
    derived: boolean
  ): void => {
    for (const claim of claims) {
      const target = resolveClaimTarget(occurrence, claim.path)
      if (target) applyPropertyClaim(occurrence, rank, target, claim)
    }
    applyInstanceLayoutScale(occurrence, source)
    applyPlacedConstraints(occurrence, base, source)
    if (source.type === 'INSTANCE' && source.size)
      occurrence.properties.size = structuredClone(source.size)
    if (derived) applyDerivedBounds(occurrence, source)
  }

  if (sources.get(rootId)?.type !== expectedType) {
    const kind = { INSTANCE: 'an instance', SYMBOL: 'a component', CANVAS: 'a page' }[expectedType]
    throw new Error(`Expected ${kind} source`)
  }
  return expand(rootId, [], [], 0)
}
